import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { Trophy, Plus, Users, ChevronRight, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CreateFriendChallengeModal from "../components/CreateFriendChallengeModal";

export default function FriendChallenges() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/friend-challenges");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    return (
        <div data-testid="friend-challenges-page">
            <TopBar title="Friend Challenges" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-6">
                <header className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-gray">Outdoor with friends</div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl text-midnight-pine tracking-tight">Friend Challenges</h1>
                        <p className="text-sm text-slate-gray mt-1 max-w-xl">
                            Run private challenges with the people you follow. Pure camaraderie — no XP stakes.
                        </p>
                    </div>
                    <button
                        onClick={() => setOpenCreate(true)}
                        data-testid="friend-challenges-new-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark active:scale-95 transition-all"
                    >
                        <Plus className="w-4 h-4" /> New challenge
                    </button>
                </header>

                {loading ? (
                    <div data-testid="fc-loading" className="bg-snowcap border border-ridge-line rounded-xl p-8 text-center">
                        <Trophy className="w-10 h-10 text-slate-gray mx-auto mb-2 animate-pulse" />
                        <p className="text-sm text-slate-gray">Loading…</p>
                    </div>
                ) : items.length === 0 ? (
                    <Empty onCreate={() => setOpenCreate(true)} />
                ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {items.map((c) => <ChallengeRow key={c.id} c={c} />)}
                    </div>
                )}
            </div>

            <CreateFriendChallengeModal
                open={openCreate}
                onOpenChange={setOpenCreate}
                onCreated={refresh}
            />
        </div>
    );
}

function Empty({ onCreate }) {
    return (
        <div data-testid="fc-empty" className="bg-snowcap border border-ridge-line rounded-xl p-10 text-center">
            <Users className="w-10 h-10 text-slate-gray mx-auto mb-3" />
            <h3 className="font-display font-bold text-lg text-midnight-pine mb-1">No challenges yet</h3>
            <p className="text-sm text-slate-gray max-w-sm mx-auto mb-4">
                Start one with your followers — "3 hikes this month" or "5 trail notes this week".
            </p>
            <button
                onClick={onCreate}
                data-testid="fc-empty-cta"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark transition-all"
            >
                <Plus className="w-4 h-4" /> Create your first challenge
            </button>
        </div>
    );
}

function ChallengeRow({ c }) {
    const me = c.leaderboard?.[0];
    const ends = c.ends_at ? new Date(c.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
    return (
        <Link
            to={`/friend-challenges/${c.id}`}
            data-testid={`fc-row-${c.id}`}
            className="block bg-snowcap border border-ridge-line rounded-xl p-4 hover:border-ember-orange transition-all"
        >
            <header className="flex items-start justify-between gap-2 mb-2">
                <div className="font-display font-bold text-base text-midnight-pine leading-tight">{c.title}</div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                    c.status === "active" ? "bg-moss/15 text-moss" : "bg-trail-tan text-granite"
                }`}>
                    {c.status}
                </span>
            </header>
            <div className="text-xs text-slate-gray mb-3 line-clamp-2">{c.description || "—"}</div>
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-3 text-slate-gray">
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.participants?.length || 0}</span>
                    <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> {c.target_count} {c.action_kind.replace(/_/g, " ")}</span>
                    {ends && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> ends {ends}</span>}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-gray" />
            </div>
        </Link>
    );
}
